import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { FPAData, FPADataFactory, FPADataTypes, IFPAData } from "../../infra/protected/FPA/FPAData";
import { doSearchThunk, getContextPathThunk, loadTreeDataThunk, updateFiltersThunk } from "./finderThunks";
import { Items } from "../../services/soap/project/responses/ListFPAResponse";
import { AtollonEvent, publish } from "../../infra/events/Events";


export interface FinderState{
    treeData: IFPAData[];
    searchData: IFPAData[];
    backupData: IFPAData[];
    childrenCache: any;
    searchCache: any;
    selectedItem: IFPAData | null;
    selectedIndex: number;
    folderStatusMapping: any;
    projectStatusMapping: any;
    activityStatusMapping: any;
    searchFilter: any;
    showOverlay: boolean;
    showGlobalFinder: boolean;
    searchParent: IFPAData | null;
    inContextMode: boolean;
    lastOpenedItem: IFPAData | null;
    search_result_offset: number;
    search_result_total: number;
    isLoading: boolean;
    isSearching: boolean;
    loadingIndex: number;
    showDetails: boolean;
    treeFocus: boolean;
}

export const initialState: FinderState = {
    treeData: [],
    searchData: [],
    backupData: [],
    childrenCache: {},
    searchCache: {},
    selectedItem: null,
    selectedIndex: -1,
    folderStatusMapping: {},
    projectStatusMapping: {},
    activityStatusMapping: {},
    searchFilter: {},
    showOverlay: false,
    showGlobalFinder: false,
    searchParent: null,
    inContextMode: false,
    lastOpenedItem: null,
    search_result_offset: 0,
    search_result_total: 0,
    isLoading: false,
    isSearching: false,
    loadingIndex: -1,
    showDetails: false,
    treeFocus: true
} 

// Helper function to convert FPAData class instances to plain IFPAData objects
// This removes class methods and circular references, making objects serializable
const toPlainObject = (data: FPAData | IFPAData): IFPAData => {
    // If it's already a plain object (not a class instance), return a copy
    if (!(data instanceof FPAData)) {
        const plain = { ...data };
        // Remove parent circular reference, keep only parent_id
        if (plain.parent) {
            // delete (plain as any).parent;
        }
        return plain;
    }
    
    // Convert class instance to plain object
    const plain: IFPAData = {
        id: data.id,
        parent_id: data.parent_id,
        parent_type: data.parent_type,
        parent: data.parent,
        title: data.title,
        count: data.count,
        type: data.type,
        line1: data.line1,
        line2: data.line2,
        statusName: data.statusName,
        statusColor: data.statusColor,
        image: data.image,
        item_type_id: data.item_type_id,
        item_type_name: data.item_type_name,
        system_type: data.system_type,
        isOpen: data.isOpen,
        isFiltered: data.isFiltered,
        isSelected: data.isSelected,
        isSearching: data.isSearching,
        page_offset: data.page_offset,
        page_limit: data.page_limit,
        node_level: data.node_level,
        extra: data.extra
    };
    
    return plain;
};

const find_remove_ids = (id:number, treeItems:IFPAData[]):number[] => {
    var ids = [];

    ids = treeItems.filter((item) => +item.parent_id! === +id).map((item) => +item.id);

    var total: number[] = [];

    ids.forEach((item) => {
        total.push(item);
        total.push(...find_remove_ids(item, treeItems));
    });

    return total;
}

const set_context = (items:IFPAData[], context:IFPAData):IFPAData[] => {
    var ids:number[] = [];

    var index = items.findIndex((item) => +item.id === +context.id);
    var visit:IFPAData | undefined = items[index];
    
    // Track items that need updating to avoid mutations
    const itemsToUpdate = new Map<number, Partial<IFPAData>>();
    
    while(visit){
        ids.push(+visit.id);
        if(visit.type !== FPADataTypes.FOLDER_TYPE){
            itemsToUpdate.set(visit.id, { isOpen: false, page_offset: 0 });
        }
        index = items.findIndex((item) => +item.id === +visit!.parent_id!);
        visit = items[index];
    }
  
    ids.push(...items.filter((item) => item.parent_id === context.id).map((item) => +item.id));
    
    // Create new array with updated items (immutable)
    return items
        .filter((item) => ids.includes(+item.id))
        .map((item) => {
            const updates = itemsToUpdate.get(item.id);
            return updates ? { ...item, ...updates } : item;
        });
}

const add_separators_of_item_type = (items:IFPAData[], parent: IFPAData):IFPAData[] => {
    var return_items:IFPAData[] = [];
    var last_type = '';

    for(var item of items){
        if(item.type !== FPADataTypes.FOLDER && item.item_type_name !== last_type){
            const time_stamp = new Date().getTime();
            // Create plain object instead of class instance
            const separatorItem: IFPAData = {
                id: item.item_type_id + time_stamp,
                parent_id: parent.id,
                parent_type: parent.type,
                title: item.item_type_name,
                count: 0,
                type: FPADataTypes.ITEM_TYPE,
                line1: '',
                line2: '',
                statusName: '',
                statusColor: '',
                image: '',
                item_type_id: item.item_type_id,
                item_type_name: item.item_type_name,
                system_type: 0,
                isOpen: false,
                isFiltered: false,
                isSelected: false,
                isSearching: false,
                page_offset: 0,
                page_limit: 50,
                node_level: 0,
                extra: null
            };
            return_items.push(separatorItem);
            last_type = item.item_type_name;
        }
        return_items.push(item);
    }

    return return_items;
}

export const finderSlice = createSlice({
    name: 'finder',
    initialState,
    reducers: {
        resetFinder: (state) => {
            state.treeData = [];
            state.childrenCache = {};
            state.selectedItem = null;
            state.searchFilter = {};
            state.showOverlay = false;
            state.searchParent = null;
            state.inContextMode = false;
            state.lastOpenedItem = null;
            state.search_result_offset = 0;
            state.search_result_total = 0;
            state.isLoading = false;
            state.isSearching = false;
            state.searchCache = {};
            state.loadingIndex = -1;
            state.showDetails = false;
        },
        changePageOffsetOfItems: (state, action: PayloadAction<{itemId:number, offset:number}>) => {
            var parent = state.treeData.find((item) => item.id === action.payload.itemId);
            if(parent){
                parent.page_offset = action.payload.offset;
            }
            if(state.lastOpenedItem && state.lastOpenedItem.id === action.payload.itemId){
                state.lastOpenedItem.page_offset = action.payload.offset;
            }
            if(state.selectedItem && state.selectedItem.id === action.payload.itemId){
                state.selectedItem.page_offset = action.payload.offset;
            }
        },
        loadFinder: (state, action: PayloadAction<{
                                        treeData:IFPAData[], 
                                        selectedItem:IFPAData | null, 
                                        showDetails:boolean, 
                                        inContextMode:boolean,
                                        lastOpenedItem:IFPAData | null
                                    }>) => {
            // Convert any class instances to plain objects
            state.treeData = action.payload.treeData.map(item => toPlainObject(item as any));
            state.selectedItem = action.payload.selectedItem ? toPlainObject(action.payload.selectedItem as any) : null;
            state.showDetails = action.payload.showDetails;
            state.inContextMode = action.payload.inContextMode;
            if(action.payload.lastOpenedItem) {
                const found = state.treeData.find( item => item.id === action.payload.lastOpenedItem!.id);
                state.lastOpenedItem = found ? found : toPlainObject(action.payload.lastOpenedItem as any);
            }
        },
        loadFolderTypes: (state, action: PayloadAction<{ folderTypes: any }>) => {
            const folder_types = Object.keys(action.payload.folderTypes).map((key:any) => action.payload.folderTypes[key]);
            // Create plain objects instead of class instances
            var _items:IFPAData[] = folder_types.map((item): IFPAData => ({
                id: item.id,
                parent_id: undefined,
                parent_type: undefined,
                title: item.name,
                count: 0,
                type: FPADataTypes.FOLDER_TYPE,
                line1: '',
                line2: '',
                statusName: '',
                statusColor: '',
                image: '',
                item_type_id: 0,
                item_type_name: '',
                system_type: 0,
                isOpen: false,
                isFiltered: false,
                isSelected: false,
                isSearching: false,
                page_offset: 0,
                page_limit: 50,
                node_level: 0,
                extra: null
            }));
            _items.sort((a:IFPAData, b:IFPAData) => a.title < b.title ? -1 : 1);
            state.treeData = _items;
            state.selectedIndex = -1;
            state.selectedItem = null;
        },
        setSelectedIndex: (state, action: PayloadAction<number>) => {
            if(state.selectedItem && state.selectedItem.isSelected){
                // Create a new mutable copy to avoid read-only property error
                state.selectedItem = { ...state.selectedItem, isSelected: false };
            }
            state.selectedIndex = action.payload;

            // Create a new mutable copy with isSelected set to true
            var newSelectedItem = { ...state.treeData[action.payload], isSelected: true };
            state.selectedItem = newSelectedItem;
            // Also update the item in treeData to keep it in sync
            state.treeData[action.payload] = newSelectedItem;
        },
        setTreeFocus: (state, action: PayloadAction<boolean>) => {
            state.treeFocus = action.payload;
        },
        moveToParent: (state) => {
            if(state.selectedItem){
                var parent_index = state.treeData.findIndex((item) => item.id === state.selectedItem!.parent_id);
                if(parent_index >= 0){
                    state.treeData[state.selectedIndex] = {...state.treeData[state.selectedIndex], isSelected: false};
                    state.treeData[parent_index] = {...state.treeData[parent_index], isSelected: true};
                    state.selectedItem = state.treeData[parent_index];
                    state.selectedIndex = parent_index;
                }
            }
        },
        nextSelection: (state) => {
            if(state.selectedIndex === -1 && state.treeData.length === 0) return;
            
            var old_index = Math.max(state.selectedIndex, 0);
            var new_index = state.selectedIndex + 1;

            while(new_index < state.treeData.length && state.treeData[new_index].type === FPADataTypes.ITEM_TYPE)
                new_index += 1;

            if(new_index >= state.treeData.length)
                new_index = 0;

            state.treeData[old_index] = {...state.treeData[old_index], isSelected: false};
            state.treeData[new_index] = {...state.treeData[new_index], isSelected: true};
            state.selectedIndex = new_index;
            // state.selectedItem = state.treeData[new_index];
        },
        previousSelection: (state) => {
            if(state.selectedIndex === -1 && state.treeData.length === 0) return;

            var old_index = Math.max(state.selectedIndex, 0);
            var new_index = state.selectedIndex - 1;

            while(new_index >= 0 && state.treeData[new_index].type === FPADataTypes.ITEM_TYPE)
                new_index -= 1;

            if(new_index < 0)
                new_index = state.treeData.length - 1;

            state.treeData[old_index] = {...state.treeData[old_index], isSelected: false};
            state.treeData[new_index] = {...state.treeData[new_index], isSelected: true};
            state.selectedIndex = new_index;
            // state.selectedItem = state.treeData[new_index];
        },
        setSelectedItemAsSelectedIndex(state) {
            state.selectedItem = state.treeData[state.selectedIndex];
        },
        setSelectedIndexAsSelectedItem(state) {
            if(state.selectedItem){
                var index = state.treeData.findIndex((item) => item.id === state.selectedItem!.id);
                state.selectedIndex = index;
            }
        },
        setSelectedItem: (state, action: PayloadAction<{item: IFPAData | null, show_context:boolean}>) => {
            var unselected_index = state.treeData.findIndex((item) => item.isSelected);
            var selected_index = state.treeData.findIndex((item) => item.id === action.payload.item?.id);

            if(unselected_index > -1){
                // Create a new mutable copy to avoid read-only property error
                state.treeData[unselected_index] = { ...state.treeData[unselected_index], isSelected: false };
            }
            if(selected_index > -1){
                // Create a new mutable copy to avoid read-only property error
                state.treeData[selected_index] = { ...state.treeData[selected_index], isSelected: true };
                if(action.payload.show_context){
                    if(state.backupData.length === 0)
                        state.backupData = [...state.treeData];
                    var new_tree = set_context([...state.treeData], action.payload.item!);
                    state.treeData = [...new_tree];
                }
            }
            // Create a copy and convert to plain object if it came from outside Redux
            state.selectedItem = action.payload.item ? toPlainObject(action.payload.item as any) : null;
            state.selectedIndex = selected_index;
            state.inContextMode = true;
        },
        resetSelection: (state) => {
            state.selectedItem = null;
        },
        setMappings: (state, action: PayloadAction<{ folderStatusMapping: any, projectStatusMapping: any, activityStatusMapping: any }>) => {
            state.folderStatusMapping = action.payload.folderStatusMapping;
            state.projectStatusMapping = action.payload.projectStatusMapping;
            state.activityStatusMapping = action.payload.activityStatusMapping;
        },
        setSearchFilter: (state, action: PayloadAction<any>) => {
            state.searchFilter = action.payload;
        },
        unloadTreeData: (state, action: PayloadAction<{itemId:number}>) => {
            var ids = find_remove_ids(action.payload.itemId, state.treeData);
            // Update items immutably
            state.treeData = state.treeData.map((item) => {
                if(ids.includes(+item.id) || item.id === action.payload.itemId){
                    return { ...item, isOpen: false, page_offset: 0 };
                }
                return item;
            });
            state.treeData = state.treeData.filter((item) => !ids.includes(+item.id));
            if(state.lastOpenedItem && state.lastOpenedItem.id === action.payload.itemId){
                state.lastOpenedItem = null;
            }
            if(state.selectedItem && state.selectedItem.id === action.payload.itemId){
                state.selectedItem = { ...state.selectedItem, page_offset: 0 };
            }
        },
        showSearch: (state, action: PayloadAction<IFPAData>) => {
            state.showOverlay = true;
            var find_item = state.treeData.find((item) => item.id === action.payload.id);
            if(find_item){
                // Create a copy to avoid reference issues
                state.searchParent = { ...find_item };
            }
        },
        showGlobalSearch: (state) => {
            state.showOverlay = true;
            state.showGlobalFinder = true;
        },
        hideSearch: (state, action:PayloadAction<boolean>) => {
            state.showOverlay = false;
            state.showGlobalFinder = false;
            
            if(action.payload && state.searchParent){
                // Update searchParent in treeData immutably
                const searchParentIndex = state.treeData.findIndex(item => item.id === state.searchParent!.id);
                if(searchParentIndex >= 0) {
                    state.treeData[searchParentIndex] = {
                        ...state.treeData[searchParentIndex],
                        isFiltered: false
                    };
                }
                state.searchParent = null;
            }
            state.searchData = [];
            state.search_result_offset = 0;
            state.search_result_total = 0;
        },
        resetSearch: (state, action: PayloadAction<IFPAData>) => {
            action.payload.isFiltered = false;
            if(action.payload.isOpen){
                action.payload.isOpen = false;
                var ids = find_remove_ids(+action.payload.id, state.treeData);
                state.treeData = state.treeData.filter((item) => !ids.includes(+item.id));
            }
            state.searchCache[action.payload.id] = null;
            if(state.lastOpenedItem && state.lastOpenedItem.id === action.payload.id)
                state.lastOpenedItem.page_offset = 0;
        },
        showSearchedSelected: (state, action: PayloadAction<IFPAData>) => {
            if(state.selectedItem) {
                // Create a new mutable copy to avoid read-only property error
                state.selectedItem = { ...state.selectedItem, isSelected: false };
            }

            var selected_item = state.treeData.find( item => item.id === action.payload.id);
            var selected_item_parent = state.treeData.find( item => item.id === action.payload.parent_id);

            // Create a new mutable copy to avoid read-only property error
            if(!selected_item) 
                state.selectedItem = { ...action.payload, isSelected: true };
            else
                state.selectedItem = { ...selected_item, isSelected: true };

            state.showDetails = true;

            if(!state.searchParent) 
                return;
                        
            var ids = find_remove_ids(state.searchParent!.id, state.treeData);
            // Update items immutably
            state.treeData = state.treeData.map((item) => {
                if(ids.includes(+item.id)){
                    return { ...item, isOpen: false };
                }
                return item;
            });

            state.treeData = state.treeData.filter((item) => !ids.includes(+item.id));
            // var insert_index = state.treeData.indexOf(state.searchParent!) + 1;
            var insert_index = state.treeData.findIndex((item) => item.id === state.searchParent!.id) + 1;

            state.treeData.splice(insert_index, 0, action.payload);

            if(selected_item_parent){
                state.selectedItem = {
                    ...state.selectedItem,
                    node_level: selected_item_parent.node_level + 1
                };
                state.treeData[insert_index] = {
                    ...state.treeData[insert_index],
                    node_level: selected_item_parent.node_level + 1
                };
            }

            // Update searchParent immutably
            if(state.searchParent) {
                const searchParentIndex = state.treeData.findIndex(item => item.id === state.searchParent!.id);
                if(searchParentIndex >= 0) {
                    state.treeData[searchParentIndex] = {
                        ...state.treeData[searchParentIndex],
                        isOpen: true,
                        isFiltered: true
                    };
                }
            }
            state.searchParent = null;
        },
        openContextMode: (state, action: PayloadAction<IFPAData>) => {
            state.inContextMode = true;
            state.backupData = [...state.treeData];
            var new_tree = set_context([...state.treeData], action.payload);
            state.treeData = new_tree;
        },
        closeContextMode: (state) => {
            state.inContextMode = false;
            state.treeData = [...state.backupData];
            state.backupData = [];
        },
        openDetails: (state) => {
            state.showDetails = true;
        },
        closeDetails: (state) => {
            state.showDetails = false;
        },
        removeItemFromTree: (state, action: PayloadAction<number>) => {
            var removed_item = state.treeData.find((item) => item.id === action.payload);
            if(removed_item){
                state.treeData = state.treeData.filter((item) => item.id !== action.payload);
                state.selectedItem = null;
                state.selectedIndex = -1;
                if(removed_item.parent){
                    var parent_index = state.treeData.findIndex((item) => item.id === removed_item!.parent_id);
                    if(parent_index > -1){
                        state.treeData[parent_index].count = state.treeData[parent_index].count - 1;
                    }
                }
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(loadTreeData.rejected, (state) => {
            state.isLoading = false;
        }).addCase(loadTreeData.pending, (state, action) => {
            state.isLoading = true;

            var parent = state.treeData.find((item) => item.id == action.meta.arg.itemId);
            if(parent){
                var children_count = state.treeData.filter((item) => item.parent_id === action.meta.arg.itemId).length;
                state.loadingIndex = state.treeData.indexOf(parent) + children_count;
            }
        }).addCase(loadTreeData.fulfilled, (state, action) => {
            state.isLoading = false;
            if(!action.payload.total_rows.ITEMS || action.payload.total_rows.ITEMS.length === 0) return;
            var parent = state.treeData.find((item) => item.id === action.meta.arg.itemId);
            
            if(!state.childrenCache[action.meta.arg.itemId]){
                state.childrenCache[action.meta.arg.itemId] = {};
            }

            const parent_node_level = parent ? parent.node_level : 0;

            state.childrenCache[action.meta.arg.itemId][action.meta.arg.offset] = [...action.payload.total_rows.ITEMS];
            // Convert factory results to plain objects and handle parent reference
            var _items = parent && parent.isFiltered && state.searchCache[action.meta.arg.itemId] ? state.searchCache[action.meta.arg.itemId] 
            : action.payload.total_rows.ITEMS.map((item:Items):IFPAData => {
                const factoryResult = FPADataFactory.createFromItem(
                    item, 
                    state.folderStatusMapping, 
                    state.projectStatusMapping, 
                    state.activityStatusMapping,
                    action.meta.arg.itemId,
                    action.meta.arg.itemType,
                    parent ? toPlainObject(parent) : undefined,
                    parent_node_level + 1
                );
                return toPlainObject(factoryResult);
            });
                
            if(parent)
                _items = add_separators_of_item_type(_items, parent!);

            if(action.meta.arg.itemId === 0){
                state.treeData = _items;
            } else {
                
                if(parent){
                    var insert_index = state.treeData.indexOf(parent) + 1;
                    if(parent.isOpen)
                        insert_index += parent.page_offset;
                    
                    // var rest_children = [ ...state.treeData];
                    var rest_children = action.meta.arg.offset == 0 ? state.treeData.filter((item) => item.parent_id !== action.meta.arg.itemId) : [ ...state.treeData];

                    rest_children.splice(insert_index, 0, ..._items);
                    // Update parent in rest_children before assigning to state to avoid read-only issues
                    var parentIndexInRest = rest_children.findIndex((item) => item.id === action.meta.arg.itemId);
                    if(parentIndexInRest >= 0){
                        // Create a new mutable copy of the parent object
                        var parentCopy = { ...rest_children[parentIndexInRest] };
                        parentCopy.isOpen = true;
                        if(parentCopy.type === FPADataTypes.FOLDER_TYPE && parentCopy.count === 0){
                            parentCopy.count = +action.payload.total_count;
                        }
                        rest_children[parentIndexInRest] = parentCopy;
                    }
                    state.treeData = rest_children;
                    // Set lastOpenedItem reference from the new state
                    if(parentIndexInRest >= 0){
                        state.lastOpenedItem = state.treeData[parentIndexInRest];
                    }
                }
            }
        });

        builder.addCase(doSearch.pending, (state, action) => {
            state.isSearching = true;
            if(action.meta.arg.offset === 0) {
                state.searchData = [];
                state.search_result_offset = 0;
                state.search_result_total = 0;
            }
        }).addCase(doSearch.rejected, (state) => {
            state.isSearching = false;
        }).addCase(doSearch.fulfilled, (state, action) => {
            state.isSearching = false;
            if(!action.payload.total_rows.ITEMS){ state.searchData = []; return; }
            // Convert factory results to plain objects
            var result = action.payload.total_rows.ITEMS.map((item:Items):IFPAData => {
                const factoryResult = FPADataFactory.createFromItem(
                    item, 
                    state.folderStatusMapping, 
                    state.projectStatusMapping, 
                    state.activityStatusMapping,
                    action.meta.arg.itemId,
                    action.meta.arg.itemType
                );
                return toPlainObject(factoryResult);
            });
            
            state.searchData = [...state.searchData, ...result];
            state.search_result_total = action.payload.total_count;
            state.search_result_offset = action.meta.arg.offset + action.meta.arg.limit;

            state.searchCache[action.meta.arg.itemId!] = state.searchData;
        });

        builder.addCase(getContextPath.fulfilled, (state, action) => {
            if(!action.payload.count || action.payload.count === 0) return;
            if(action.meta.arg.isForSearch){
                var selected_index = state.treeData.findIndex((item) => +item.id === +state.selectedItem!.id);
                if(selected_index > -1){ return; } // if item already searched and found, do nothing

                var index = state.treeData.findIndex((item) => +item.id === +action.payload.LIST[0].CONTEXT.type);

                if(index === -1) return;

                var parent = state.treeData[index];
                
                // Update parent in state immutably
                state.treeData[index] = { ...parent, isOpen: false, isFiltered: false };
                
                // Update selectedItem immutably without parent circular reference
                state.selectedItem = {
                    ...state.selectedItem!,
                    node_level: parent.node_level + 1,
                    parent_id: parent.id,
                    parent_type: FPADataTypes.FOLDER_TYPE
                };
                state.searchCache[parent.id] = [state.selectedItem];
                state.treeData.splice(index + 1, 0, state.selectedItem);
                var new_tree = set_context([...state.treeData], state.selectedItem!);
                state.treeData = new_tree;
                state.showDetails = true;
                state.inContextMode = true;
            } else {
                // Convert factory results to plain objects
                var items = action.payload.LIST.map((item):IFPAData => {
                    const factoryResult = FPADataFactory.createFromContextItem(
                        item.CONTEXT, 
                        state.folderStatusMapping, 
                        state.projectStatusMapping, 
                        state.activityStatusMapping
                    );
                    return toPlainObject(factoryResult);
                });
                
                items.reverse();
                // Update items with parent_id instead of parent object to avoid circular references
                items.forEach((item:IFPAData, index:number) => {
                  item.isOpen = false;
                  item.isFiltered = false;
                  // Use parent_id instead of parent object
                  item.parent_id = index > 0 ? items[index - 1].id : undefined;
                  item.parent_type = index > 0 ? items[index - 1].type : undefined;
                  item.parent = index > 0 ? items[index - 1] : undefined;
                  item.node_level = index;
                });
                state.treeData = items;
                // Create a new mutable copy to avoid read-only property error
                var lastItem = items[items.length - 1];
                state.selectedItem = { ...lastItem, isSelected: true, isOpen: false, isFiltered: false };
                // Also update the item in treeData to keep it in sync
                items[items.length - 1] = state.selectedItem;
                state.inContextMode = true;
                publish(AtollonEvent.INFO_CHANGED, state.selectedItem.title);
            }
        });

        builder.addCase(updateFilters.fulfilled, (state, action) => {
            //state.searchFilter = action.payload;
            console.log('Filters updated: ', action.payload);
        });
    }
});

export const loadTreeData = createAsyncThunk(
    'finder/loadData',
    loadTreeDataThunk,
    {
        condition: (arg, {getState}) => {
            var { finder } = getState() as any;
            return !finder.isLoading;
        }
    }
);

export const doSearch = createAsyncThunk(
    'finder/doSearch',
    doSearchThunk
);

export const getContextPath = createAsyncThunk(
    'finder/getContextPath',
    getContextPathThunk
);

export const updateFilters = createAsyncThunk(
    'finder/updateFilters',
    updateFiltersThunk
);

export const { 
    changePageOffsetOfItems,
    resetFinder,
    loadFinder,
    loadFolderTypes,
    setSelectedItem,
    setSelectedIndex,
    nextSelection,
    previousSelection,
    resetSelection,
    setMappings,
    setSearchFilter,
    unloadTreeData,
    showSearch,
    showGlobalSearch,
    hideSearch,
    resetSearch,
    showSearchedSelected,
    openContextMode,
    closeContextMode,
    openDetails,
    closeDetails,
    setSelectedItemAsSelectedIndex,
    setSelectedIndexAsSelectedItem,
    moveToParent,
    setTreeFocus,
    removeItemFromTree
} = finderSlice.actions;
export default finderSlice.reducer;